/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorIcon from '@mui/icons-material/Error'
import WarningIcon from '@mui/icons-material/Warning'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AssignmentIcon from '@mui/icons-material/Assignment'
import CloseIcon from '@mui/icons-material/Close'
import VisibilityIcon from '@mui/icons-material/Visibility'

/**
 * Format error messages to be user-friendly
 * Since the backend now sends localized messages, we just extract and display them
 */
function formatErrorMessage(error, t) {
  // Handle nested errors array from backend (e.g., { filename, parkNumber, errors: [...] })
  if (error.errors && Array.isArray(error.errors) && error.errors.length > 0) {
    // Extract all error messages from the nested errors array
    const errorMessages = error.errors
      .map((e) => e.error || e.message)
      .filter((msg) => msg)

    if (errorMessages.length > 0) {
      // Join multiple errors with line breaks
      return errorMessages.join(', ')
    }
  }

  // Extract the error message from the error object
  const errorText = error.error || error.message || ''

  // If we have an error message, return it
  if (errorText) {
    return errorText
  }

  // If it's a JSON object without a message, try to extract meaningful info
  if (typeof error === 'object') {
    if (error.code) {
      return t('errors.error-code', {
        code: error.code,
        defaultValue: `Erreur : ${error.code}`,
      })
    }
  }

  // Last resort: generic error message
  return t('errors.unknown-error', {
    defaultValue: "Une erreur inconnue s'est produite",
  })
}

ResultsStep.propTypes = {
  results: PropTypes.object,
  processingStatus: PropTypes.object,
  onCheckProgress: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  documentTypes: PropTypes.array,
  onReassignDocumentType: PropTypes.func,
  onGetPreviewUrl: PropTypes.func,
  onUnassignedCountChange: PropTypes.func,
}

export default function ResultsStep({
  results,
  processingStatus,
  onCheckProgress,
  onClose,
  documentTypes = [],
  onReassignDocumentType,
  onGetPreviewUrl,
  onUnassignedCountChange,
}) {
  const { t } = useTranslation()
  const [isPolling, setIsPolling] = useState(false)
  const isPausedRef = React.useRef(false)
  const retryCountRef = React.useRef(0)
  const pauseTimeoutRef = React.useRef(null)
  const onCheckProgressRef = React.useRef(onCheckProgress)
  const [otherDocuments, setOtherDocuments] = useState([])
  const [selectedTypes, setSelectedTypes] = useState({})
  const [ignoredDocuments, setIgnoredDocuments] = useState(new Set())
  const [assignedDocuments, setAssignedDocuments] = useState(new Map()) // Map<docKey, { typeId, typeName }>

  // Calculate and report unassigned unrecognized documents count
  React.useEffect(() => {
    if (onUnassignedCountChange) {
      // Count documents that are NOT assigned and NOT ignored
      const unassignedCount = otherDocuments.filter((doc) => {
        const docKey = `${doc.filename}-${doc.group}`
        const isAssigned = assignedDocuments.has(docKey)
        const isIgnored = ignoredDocuments.has(doc.group)
        return !isAssigned && !isIgnored
      }).length

      onUnassignedCountChange(unassignedCount)
    }
  }, [
    otherDocuments,
    assignedDocuments,
    ignoredDocuments,
    onUnassignedCountChange,
  ])

  const handlePreview = async (doc) => {
    try {
      const batchId = results?.batchId || processingStatus?.batchId
      if (onGetPreviewUrl && batchId) {
        // Pass the page indices to generate a preview with only those pages
        const previewUrl = await onGetPreviewUrl(
          batchId,
          doc.filename,
          doc.pages
        )
        // Open the preview PDF (contains only the unrecognized pages)
        window.open(previewUrl, '_blank')
      }
    } catch (error) {
      console.error('Error getting preview URL:', error)
    }
  }

  // Keep the callback ref up to date
  useEffect(() => {
    onCheckProgressRef.current = onCheckProgress
  }, [onCheckProgress])

  // Parse errors to extract "other" documents
  useEffect(() => {
    if (!results || !results.errors) return

    const others = []

    // Flatten nested errors structure
    // results.errors structure: [{ filename, parkNumber, errors: [...] }]
    results.errors.forEach((batchError) => {
      // Check if this batch error has nested errors array
      if (batchError.errors && Array.isArray(batchError.errors)) {
        batchError.errors.forEach((error) => {
          // Check if error message indicates unrecognized document type
          // Supports both French and English error messages
          const isUnrecognized =
            error.error &&
            (error.error.includes('Document non reconnu automatiquement') ||
              error.error.includes('Document type not found in database'))

          if (isUnrecognized) {
            // For French message, documentType should be extracted from group or pages info
            // For English message, try to parse from the message
            let documentTypeName = t('common.unknown', 'Inconnu')

            const englishMatch = error.error.match(
              /Document type not found in database: (.+)/
            )
            if (englishMatch) {
              documentTypeName = englishMatch[1]
            }

            others.push({
              filename: error.filename || batchError.filename,
              documentType: documentTypeName,
              pages: error.pages || [],
              group: error.group,
              originalError: error,
              batchId: results.batchId || processingStatus?.batchId,
            })
          }
        })
      } else {
        // Fallback: check if the batch error itself is the error (old format)
        const isUnrecognized =
          batchError.error &&
          (batchError.error.includes('Document non reconnu automatiquement') ||
            batchError.error.includes('Document type not found in database'))

        if (isUnrecognized) {
          let documentTypeName = t('common.unknown', 'Inconnu')

          const englishMatch = batchError.error.match(
            /Document type not found in database: (.+)/
          )
          if (englishMatch) {
            documentTypeName = englishMatch[1]
          }

          others.push({
            filename: batchError.filename,
            documentType: documentTypeName,
            pages: batchError.pages || [],
            group: batchError.group,
            originalError: batchError,
            batchId: results.batchId || processingStatus?.batchId,
          })
        }
      }
    })

    setOtherDocuments(others)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, processingStatus])

  // Poll for results if still processing
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const isStillProcessing = results && results.status === 'processing'

    if (isStillProcessing && onCheckProgress) {
      setIsPolling(true)

      const interval = setInterval(async () => {
        // Skip if paused
        if (isPausedRef.current) {
          return
        }

        const timeoutMs = 3000 // 3 second timeout to detect queued requests
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('REQUEST_TIMEOUT')), timeoutMs)
        })

        try {
          // Race between the actual request and timeout
          await Promise.race([onCheckProgressRef.current(), timeoutPromise])

          // Reset retry count on successful poll
          if (retryCountRef.current > 0) {
            retryCountRef.current = 0
          }
        } catch (error) {
          // Check if request is hanging (likely queued by auth retrier)
          if (error?.message === 'REQUEST_TIMEOUT') {
            isPausedRef.current = true
            retryCountRef.current += 1

            // Clear any existing pause timeout
            if (pauseTimeoutRef.current) {
              clearTimeout(pauseTimeoutRef.current)
            }

            // Wait before resuming (exponential backoff)
            const backoffMs = Math.min(
              2000 * 2 ** (retryCountRef.current - 1),
              10000
            )

            pauseTimeoutRef.current = setTimeout(() => {
              isPausedRef.current = false
              pauseTimeoutRef.current = null
            }, backoffMs)
          } else if (error?.response?.status === 401) {
            // Direct 401 error (shouldn't happen with auth retrier, but just in case)
            isPausedRef.current = true
          } else {
            console.error('[ResultsStep] Polling error:', error)
          }
        }
      }, 1000) // Poll every 1 second

      return () => {
        clearInterval(interval)
        setIsPolling(false)
        if (pauseTimeoutRef.current) {
          clearTimeout(pauseTimeoutRef.current)
        }
      }
    }
    setIsPolling(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results?.status])

  if (!results && isPolling) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          {t(
            'components.multi-document-import-dialog.waiting-for-results',
            'Waiting for results...'
          )}
        </Typography>
      </Box>
    )
  }

  if (!results) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body1">
          {t(
            'components.multi-document-import-dialog.no-results-yet',
            'Processing in progress. Results will appear here when completed.'
          )}
        </Typography>
        <Button variant="outlined" onClick={onClose} sx={{ mt: 2 }}>
          {t('common.close', 'Close')}
        </Button>
      </Box>
    )
  }

  const { status, progress = {}, items = [], errors = [] } = results

  const isCompleted = status === 'completed'
  const isFailed = status === 'failed'
  const {
    totalFiles = 0,
    processedFiles = 0,
    totalDocumentsCreated = 0,
    errorCount = 0,
  } = progress

  // Helper function to check if an error is an unrecognized document (not a real error)
  const isUnrecognizedDocumentError = (error) => {
    // Check nested errors array
    if (error.errors && Array.isArray(error.errors)) {
      return error.errors.every(
        (e) =>
          e.error &&
          (e.error.includes('Document non reconnu automatiquement') ||
            e.error.includes('Document type not found in database'))
      )
    }
    // Check direct error message
    return (
      error.error &&
      (error.error.includes('Document non reconnu automatiquement') ||
        error.error.includes('Document type not found in database'))
    )
  }

  // Filter errors to separate real errors from unrecognized documents
  // Also exclude assigned unrecognized documents
  const realErrors = errors.filter((error) => {
    // Skip all unrecognized document errors (handled in separate section)
    if (isUnrecognizedDocumentError(error)) {
      return false
    }

    // Include real errors (not unrecognized documents)
    return true
  })

  // Filter otherDocuments to exclude assigned ones when counting for hasOnlyUnrecognizedDocs
  const unassignedOtherDocs = otherDocuments.filter((doc) => {
    const docKey = `${doc.filename}-${doc.group}`
    return !assignedDocuments.has(docKey) && !ignoredDocuments.has(doc.group)
  })

  const hasOnlyUnrecognizedDocs =
    errors.length > 0 &&
    realErrors.length === 0 &&
    unassignedOtherDocs.length > 0
  const hasRealErrors = realErrors.length > 0

  return (
    <Box>
      {isCompleted && errorCount === 0 && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <AlertTitle>{t('common.success', 'Success')}</AlertTitle>
          {t('components.multi-document-import-dialog.processing-complete', {
            count: totalDocumentsCreated,
            defaultValue: `Processing completed successfully! {{count}} documents created.`,
          })}
        </Alert>
      )}

      {isCompleted && errorCount > 0 && hasOnlyUnrecognizedDocs && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>{t('common.warning', 'Warning')}</AlertTitle>
          {t(
            'components.multi-document-import-dialog.processing-complete-with-unrecognized',
            {
              count: totalDocumentsCreated,
              unrecognized: otherDocuments.length,
              defaultValue: `Processing completed! {{count}} documents created. {{unrecognized}} page(s) require manual document type assignment.`,
            }
          )}
        </Alert>
      )}

      {isCompleted && errorCount > 0 && hasRealErrors && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>{t('common.error', 'Error')}</AlertTitle>
          {t(
            'components.multi-document-import-dialog.processing-complete-with-errors',
            {
              successful: processedFiles - errorCount,
              errors: realErrors.length,
              defaultValue: `Processing completed with {{errors}} error(s). {{successful}} file(s) processed successfully.`,
            }
          )}
        </Alert>
      )}

      {isFailed && hasRealErrors && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>{t('common.error', 'Error')}</AlertTitle>
          {t(
            'components.multi-document-import-dialog.processing-failed',
            'Processing failed. Please check the errors below.'
          )}
        </Alert>
      )}

      {isFailed && hasOnlyUnrecognizedDocs && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>{t('common.warning', 'Warning')}</AlertTitle>
          {t(
            'components.multi-document-import-dialog.processing-complete-with-unrecognized',
            {
              count: totalDocumentsCreated,
              unrecognized: otherDocuments.length,
              defaultValue: `Processing completed! {{count}} documents created. {{unrecognized}} page(s) require manual document type assignment.`,
            }
          )}
        </Alert>
      )}

      {/* Summary Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Chip
          icon={<CheckCircleIcon />}
          label={`${processedFiles}/${totalFiles} ${t(
            'common.files',
            'Files'
          )} ${t('common.processed', 'Processed')}`}
          color="primary"
        />
        <Chip
          icon={<CheckCircleIcon />}
          label={`${totalDocumentsCreated} ${t(
            'common.documents',
            'Documents'
          )} ${t('common.created', 'Created')}`}
          color="success"
        />
        {otherDocuments.length > 0 && (
          <Chip
            icon={<WarningAmberOutlinedIcon />}
            label={`${otherDocuments.length} ${t(
              'common.unrecognized',
              'Unrecognized'
            )}`}
            sx={{
              backgroundColor: '#fff3e0',
              color: '#663c00',
              '& .MuiChip-icon': {
                color: '#e65100',
              },
            }}
          />
        )}
        {hasRealErrors && (
          <Chip
            icon={<ErrorIcon />}
            label={`${realErrors.length} ${t('common.errors', 'Errors')}`}
            color="error"
          />
        )}
      </Box>

      {/* Processed Items Table */}
      {items.length > 0 && (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('common.filename', 'Filename')}</TableCell>
                <TableCell>{t('common.status', 'Status')}</TableCell>
                <TableCell align="right">
                  {t('common.documents-created', 'Documents Created')}
                </TableCell>
                <TableCell align="right">
                  {t('common.pages', 'Pages')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => {
                // Check if this item's errors are only unrecognized documents
                let itemHasOnlyUnrecognizedDocs = false
                let itemHasRealErrors = false
                let allUnrecognizedAssigned = false

                if (item.status === 'failed' && item.errorMessage) {
                  try {
                    const itemErrors = JSON.parse(item.errorMessage)
                    if (Array.isArray(itemErrors) && itemErrors.length > 0) {
                      // Check if ALL errors are unrecognized documents
                      itemHasOnlyUnrecognizedDocs = itemErrors.every(
                        (err) =>
                          err.error &&
                          (err.error.includes(
                            'Document non reconnu automatiquement'
                          ) ||
                            err.error.includes(
                              'Document type not found in database'
                            ))
                      )

                      // Check if ANY error is a real error (not unrecognized)
                      itemHasRealErrors = itemErrors.some(
                        (err) =>
                          err.error &&
                          !(
                            err.error.includes(
                              'Document non reconnu automatiquement'
                            ) ||
                            err.error.includes(
                              'Document type not found in database'
                            )
                          )
                      )

                      // If only unrecognized docs, check if all have been assigned
                      if (itemHasOnlyUnrecognizedDocs) {
                        // Check if all unrecognized documents for this file have been assigned
                        const itemUnrecognizedDocs = otherDocuments.filter(
                          (doc) => doc.filename === item.filename
                        )
                        if (itemUnrecognizedDocs.length > 0) {
                          allUnrecognizedAssigned = itemUnrecognizedDocs.every(
                            (doc) => {
                              const docKey = `${doc.filename}-${doc.group}`
                              return (
                                assignedDocuments.has(docKey) ||
                                ignoredDocuments.has(doc.group)
                              )
                            }
                          )
                        }
                      }
                    }
                  } catch (e) {
                    // If parsing fails, treat as real error
                    itemHasRealErrors = true
                    itemHasOnlyUnrecognizedDocs = false
                  }
                }

                return (
                  <TableRow key={index}>
                    <TableCell>{item.filename}</TableCell>
                    <TableCell>
                      {item.status === 'completed' ||
                      (item.status === 'failed' &&
                        itemHasOnlyUnrecognizedDocs &&
                        allUnrecognizedAssigned) ? (
                        <Chip
                          label={t('common.completed', 'Completed')}
                          size="small"
                          color="success"
                          icon={<CheckCircleIcon />}
                        />
                      ) : item.status === 'failed' && itemHasRealErrors ? (
                        <Chip
                          label={t('common.failed', 'Failed')}
                          size="small"
                          color="error"
                          icon={<ErrorIcon />}
                        />
                      ) : item.status === 'failed' &&
                        itemHasOnlyUnrecognizedDocs ? (
                        <Chip
                          label={t('common.warning', 'Warning')}
                          size="small"
                          icon={<WarningAmberOutlinedIcon />}
                          sx={{
                            backgroundColor: '#fff3e0',
                            color: '#663c00',
                            '& .MuiChip-icon': {
                              color: '#e65100',
                            },
                          }}
                        />
                      ) : item.status === 'failed' ? (
                        <Chip
                          label={t('common.failed', 'Failed')}
                          size="small"
                          color="error"
                          icon={<ErrorIcon />}
                        />
                      ) : (
                        <Chip
                          label={item.status}
                          size="small"
                          icon={<WarningIcon />}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {item.documentsCreated || 0}
                    </TableCell>
                    <TableCell align="right">{item.pageCount || 0}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Errors Accordion - Only show real errors, not unrecognized documents */}
      {realErrors.length > 0 && (
        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>
              {t('common.errors', 'Errors')} ({realErrors.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              {realErrors.map((error, index) => (
                <Alert key={index} severity="error" sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    <strong>
                      {error.filename ||
                        t('common.unknown-file', 'Unknown file')}
                      :
                    </strong>{' '}
                    {formatErrorMessage(error, t)}
                  </Typography>
                </Alert>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Unrecognized Documents Section */}
      {otherDocuments.length > 0 && (
        <Accordion sx={{ mt: 2 }} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningAmberOutlinedIcon color="warning" />
              <Typography>
                {t(
                  'components.multi-document-import-dialog.unrecognized-documents',
                  'Unrecognized Documents'
                )}{' '}
                (
                {
                  otherDocuments.filter(
                    (doc) => !ignoredDocuments.has(doc.group)
                  ).length
                }
                )
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  {t(
                    'components.multi-document-import-dialog.unrecognized-documents-help',
                    'The following documents could not be automatically recognized. Please assign a document type or ignore them.'
                  )}
                </Typography>
              </Alert>

              {otherDocuments.map((doc, index) => {
                const docKey = `${doc.filename}-${doc.group}`
                const isIgnored = ignoredDocuments.has(doc.group)
                const isAssigned = assignedDocuments.has(docKey)
                const assignedInfo = assignedDocuments.get(docKey)

                if (isIgnored) return null

                return (
                  <Paper
                    key={index}
                    sx={{
                      p: 2,
                      mb: 2,
                      bgcolor: isAssigned ? '#e8f5e9' : 'background.default',
                      border: isAssigned ? '1px solid #4caf50' : 'none',
                      opacity: isAssigned ? 0.9 : 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      {isAssigned && (
                        <CheckCircleOutlineIcon
                          sx={{ color: '#4caf50', fontSize: 32, mt: 0.5 }}
                        />
                      )}

                      <Box sx={{ flex: '1 1 250px', minWidth: 0 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          <strong>{doc.filename}</strong>
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t('common.detected-as', 'Detected as')}: &quot;
                          {doc.documentType}&quot;
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t('common.pages', 'Pages')}:{' '}
                          {doc.pages.map((p) => p + 1).join(', ')}
                        </Typography>
                        {isAssigned && (
                          <Typography
                            variant="body2"
                            sx={{ color: '#4caf50', fontWeight: 'bold', mt: 1 }}
                          >
                            ✓ {t('common.assigned', 'Assigned')}:{' '}
                            {assignedInfo?.typeName}
                          </Typography>
                        )}
                      </Box>

                      <FormControl
                        sx={{ minWidth: 180, flex: '0 1 auto' }}
                        size="small"
                        disabled={isAssigned}
                      >
                        <InputLabel>
                          {t('common.document-type', 'Document Type')}
                        </InputLabel>
                        <Select
                          value={
                            isAssigned
                              ? assignedInfo?.typeId
                              : selectedTypes[docKey] || ''
                          }
                          onChange={(e) =>
                            setSelectedTypes({
                              ...selectedTypes,
                              [docKey]: e.target.value,
                            })
                          }
                          label={t('common.document-type', 'Document Type')}
                          disabled={isAssigned}
                        >
                          {(() => {
                            if (!documentTypes || documentTypes.length === 0) {
                              return (
                                <MenuItem disabled>
                                  {t(
                                    'common.no-options',
                                    'No options available'
                                  )}
                                </MenuItem>
                              )
                            }

                            // Deduplicate document types (API returns join table with duplicates)
                            const uniqueTypes = new Map()
                            documentTypes.forEach((type) => {
                              const docType = type.documentType || type
                              const docId = type.documentTypeId || docType.id
                              const docName = docType.name

                              if (docId && docName && !uniqueTypes.has(docId)) {
                                uniqueTypes.set(docId, {
                                  id: docId,
                                  name: docName,
                                })
                              }
                            })

                            // Sort by name and render
                            return Array.from(uniqueTypes.values())
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map((docType) => (
                                <MenuItem key={docType.id} value={docType.id}>
                                  {docType.name}
                                </MenuItem>
                              ))
                          })()}
                        </Select>
                      </FormControl>

                      {!isAssigned && (
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1,
                            flexWrap: 'wrap',
                            flex: '0 1 auto',
                          }}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handlePreview(doc)}
                          >
                            {t('common.preview', 'Preview')}
                          </Button>

                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<AssignmentIcon />}
                            disabled={
                              !selectedTypes[docKey] || !onReassignDocumentType
                            }
                            onClick={async () => {
                              if (
                                onReassignDocumentType &&
                                selectedTypes[docKey]
                              ) {
                                try {
                                  // Get batchId from results or processingStatus
                                  const batchId =
                                    results?.batchId ||
                                    processingStatus?.batchId
                                  await onReassignDocumentType(
                                    doc,
                                    selectedTypes[docKey],
                                    batchId
                                  )

                                  // On success, mark as assigned
                                  // Find the document type name
                                  const selectedTypeId = selectedTypes[docKey]
                                  const docType = documentTypes.find(
                                    (dt) =>
                                      (dt.documentTypeId ||
                                        dt.documentType?.id ||
                                        dt.id) === selectedTypeId
                                  )
                                  const typeName =
                                    docType?.documentType?.name ||
                                    docType?.name ||
                                    t('common.unknown', 'Inconnu')

                                  // Create new Map and add the assignment
                                  const newAssignedDocs = new Map(
                                    assignedDocuments
                                  )
                                  newAssignedDocs.set(docKey, {
                                    typeId: selectedTypeId,
                                    typeName,
                                  })
                                  setAssignedDocuments(newAssignedDocs)
                                } catch (error) {
                                  // Error handling is done in the parent component
                                  console.error(
                                    'Error assigning document:',
                                    error
                                  )
                                }
                              }
                            }}
                          >
                            {t('common.assign', 'Assign')}
                          </Button>

                          <IconButton
                            size="small"
                            onClick={() => {
                              setIgnoredDocuments(
                                new Set([...ignoredDocuments, doc.group])
                              )
                            }}
                            title={t('common.ignore', 'Ignore')}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                )
              })}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={onClose}>
          {t('common.close', 'Close')}
        </Button>
      </Box>
    </Box>
  )
}
